import _ from 'underscore';
import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { DefaultButton, Panel, PanelType, Stack, PrimaryButton, MessageBar, MessageBarType } from '@fluentui/react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';

import { IconButton } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FluentImagePicker } from '@bingads-webui-campaign-react/fluent-image-picker';
import { panelStylesProp } from '@bingads-webui-campaign-react/asset-library-shared-add-media-layout';
import { CopilotTab, CopilotTabItem } from '@bingads-webui/inline-copilot-ui';

import { getImageSuggestionPanelStyles } from './image-recommendations-panel-styles';

const getImageWithDefaultSelectState = images => _.map(images, image => _.extend({}, image, { isSelected: false }));

const mapSelectSate = (allImages, needUpdateImages) => _.map(needUpdateImages, (image) => {
  const newImage = _.find(allImages, img => img.fullUrl === image.fullUrl);
  return newImage || image;
});

export const ImageRecommendationsPanel = (props) => {
  const {
    i18n,
    title,
    images,
    themes,
    hasMore,
    loadNextPage,
    onDismiss,
    onAdd,
    maxUploadNum,
  } = props;

  const defaultImagesWithSelectState = useMemo(() => getImageWithDefaultSelectState(images), [images]);
  const classes = useDefaultStyles(getImageSuggestionPanelStyles);
  const [error, setError] = useState(null);
  const [allImages, setAllImages] = useState(defaultImagesWithSelectState);
  const [activeImages, setActiveImages] = useState(allImages);
  const [activeTheme, setActiveTheme] = useState(i18n.getString(_TL_('All')));
  const themeNames = useMemo(() => [
    ..._.map(themes, ({ name }) => name),
  ], [themes]);

  const onSwitchTheme = useCallback((key) => {
    setActiveTheme(key);
    if (key === i18n.getString(_TL_('All'))) {
      setActiveImages(allImages);
    } else {
      const rawImages = _.chain(themes)
        .filter(theme => theme.name === key)
        .first()
        .result('images')
        .map(img => _.extend({}, img))
        .value();
      const imagesWithState = mapSelectSate(allImages, rawImages);
      setActiveImages(imagesWithState);
    }
  }, [allImages, i18n, themes]);

  const onClickAdd = useCallback(() => {
    onAdd(_.filter(allImages, image => image.isSelected));
  }, [onAdd, allImages]);

  const onClickCancel = useCallback((e) => {
    onDismiss(e);
  }, [onDismiss]);

  const onRenderHeader = useCallback(() => (
    <div className={classes.panelHeader}>
      <div className={classes.backButton}>
        <IconButton iconProps={{ iconName: 'Back' }} className="back-button" onClick={onClickCancel} />
      </div>
      <div className={classes.panelTitle}>
        {i18n.getString(title)}
      </div>
    </div>
  ), [classes.backButton, classes.panelHeader, classes.panelTitle, i18n, onClickCancel, title]);

  const onRenderFooterContent = useCallback(() => (
    <Stack className={classnames('image-recommendations-panel-buttons', classes.buttonContainer)} horizontal tokens={{ childrenGap: 8 }}>

      <Stack.Item key="btn-add">
        <PrimaryButton className="btn-add" onClick={onClickAdd} disabled={_.findIndex(allImages, image => image.isSelected) === -1}>
          {i18n.getString(_TL_('Add'))}
        </PrimaryButton>
      </Stack.Item>
      <Stack.Item key="btn-cancel">
        <DefaultButton className="btn-cancel" onClick={onClickCancel}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </Stack.Item>
      <Stack.Item key="selected-label">
        <span className="selected-label">{i18n.getString(_TL_('Selected images: {{count}}'), { count: _.where(allImages, { isSelected: true }).length })}</span>
      </Stack.Item>
    </Stack>
  ), [classes.buttonContainer, onClickAdd, allImages, i18n, onClickCancel]);

  const onSelectImageInternal = useCallback((index) => {
    const { fullUrl } = activeImages[index];

    const image = _.find(allImages, img => img.fullUrl === fullUrl);

    if (!image.isSelected && maxUploadNum !== undefined
      && _.filter(allImages, img => img.isSelected).length >= maxUploadNum) {
      setError(i18n.getString(_TL_('You\'ve exceeded the max. number of assets you can add at a time ({{limitNumber}}). Please try again.'), { limitNumber: maxUploadNum }));
      return;
    }

    image.isSelected = !image.isSelected;
    setError(null);
    setAllImages([...allImages]);
    setActiveImages(mapSelectSate(allImages, activeImages));
  }, [activeImages, allImages, maxUploadNum, i18n]);

  return (
    <Panel
      onRenderHeader={onRenderHeader}
      isOpen
      onDismiss={onClickCancel}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      type={PanelType.custom}
      customWidth="920px"
      styles={panelStylesProp}
      className="image-recommendations-panel"
    >
      <FluentProvider theme={webLightTheme}>
        <div className={classes.theme}>
          <CopilotTab
            active={activeTheme}
            onActiveChange={key => onSwitchTheme(key)}
          >
            <CopilotTabItem tabKey={i18n.getString(_TL_('All'))}>
              {i18n.getString(_TL_('All'))}
            </CopilotTabItem>
            {_.map(themeNames, name => (
              <CopilotTabItem tabKey={name}>
                {name}
              </CopilotTabItem>
              ))}
          </CopilotTab>
        </div>
      </FluentProvider>
      <FluentImagePicker
        images={activeImages}
        pageSize={30}
        loadMoreImages={loadNextPage}
        onSelectImage={onSelectImageInternal}
        hasMoreImages={hasMore}
        imageCellWidth={260}
        imageCellHeight={243}
        isMultiSelect
      />
      {
        error && <MessageBar messageBarType={MessageBarType.error} className={classes.errorMessage}>{error}</MessageBar>
      }
    </Panel>);
};

ImageRecommendationsPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  hasMore: PropTypes.bool.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({
    thumbnailSrc: PropTypes.string,
    fullUrl: PropTypes.string,
    suggestionId: PropTypes.string,
  })).isRequired,
  loadNextPage: PropTypes.func.isRequired,
  maxUploadNum: PropTypes.number,
  themes: PropTypes.arrayOf(PropTypes.shape({})),
  title: PropTypes.string,
  onAdd: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

ImageRecommendationsPanel.defaultProps = {
  title: _TL_('Recommended images'),
  themes: [],
  maxUploadNum: undefined,
};
